// src/hooks/notificationHooks.js
import { useCallback } from 'react';
import NotificationManager from '../utils/notificationManager';
import { toast } from 'react-hot-toast';

const useNotificationActions = () => {
  
  // Notificación cuando se recibe una postulación
  const onJobApplicationReceived = useCallback(async (applicationData) => {
    try {
      console.log('🔔 Hook: Nueva postulación recibida', applicationData);
      
      const { 
        jobCreatorId, 
        applicantName, 
        jobTitle, 
        applicationId 
      } = applicationData;
      
      if (!jobCreatorId || !applicantName || !jobTitle || !applicationId) {
        console.warn('⚠️ Datos incompletos para notificación de postulación:', applicationData);
        return;
      }
      
      // Usar la función correcta: createNotification (individual)
      await NotificationManager.createNotification({
        userId: jobCreatorId,
        title: 'Nueva postulación recibida',
        message: `${applicantName} se ha postulado a tu oferta "${jobTitle}"`,
        notificationType: 'job_application',
        relatedEntityType: 'job_application',
        relatedEntityId: applicationId
      });
      
      console.log('✅ Notificación de postulación enviada exitosamente');
      
    } catch (error) {
      console.error('❌ Error en hook onJobApplicationReceived:', error);
      // No mostrar toast de error al usuario final para no confundir
    }
  }, []);
  
  // Notificación cuando cambia el estado de una postulación
  const onJobApplicationStatusChanged = useCallback(async (statusData) => {
    try {
      console.log('🔔 Hook: Estado de postulación cambiado', statusData);
      
      const { 
        applicantId, 
        jobTitle, 
        newStatus, 
        applicationId 
      } = statusData;
      
      if (!applicantId || !jobTitle || !newStatus || !applicationId) {
        console.warn('⚠️ Datos incompletos para notificación de estado:', statusData);
        return;
      }
      
      const statusMessages = {
        'pending': 'Tu postulación está siendo revisada',
        'reviewed': 'Tu postulación ha sido revisada',
        'contacted': 'El empleador se pondrá en contacto contigo',
        'accepted': '¡Felicitaciones! Tu postulación ha sido aceptada',
        'rejected': 'Tu postulación ha sido rechazada'
      };
      
      const message = statusMessages[newStatus] || `Estado de tu postulación actualizado: ${newStatus}`;
      
      await NotificationManager.createNotification({
        userId: applicantId,
        title: 'Actualización de postulación',
        message: `${message} para la oferta "${jobTitle}"`,
        notificationType: 'job_application_update',
        relatedEntityType: 'job_application',
        relatedEntityId: applicationId
      });
      
      console.log('✅ Notificación de cambio de estado enviada exitosamente');
      
    } catch (error) {
      console.error('❌ Error en hook onJobApplicationStatusChanged:', error);
    }
  }, []);
  
  // Notificación de bienvenida para nuevos usuarios
  const onUserRegistered = useCallback(async (userData) => {
    try {
      console.log('🔔 Hook: Usuario registrado', userData);
      
      const { userId, userType, email } = userData;
      
      if (!userId) {
        console.warn('⚠️ userId faltante para notificación de bienvenida');
        return;
      }
      
      const welcomeMessages = {
        individual: 'Bienvenido a Avlink Network. Explora cursos, eventos y oportunidades laborales.',
        company: 'Bienvenido a Avlink Network. Publica ofertas de trabajo y conecta con talento AV.'
      };
      
      const message = welcomeMessages[userType] || 'Bienvenido a Avlink Network, la comunidad de profesionales audiovisuales.';
      
      await NotificationManager.createNotification({
        userId,
        title: '¡Bienvenido a Avlink Network!',
        message,
        notificationType: 'welcome',
        relatedEntityType: 'user_registration',
        relatedEntityId: userId
      });
      
      console.log('✅ Notificación de bienvenida enviada exitosamente');
      
    } catch (error) {
      console.error('❌ Error en hook onUserRegistered:', error);
    }
  }, []);
  
  // Notificación para eventos
  const onEventRegistration = useCallback(async (eventData) => {
    try {
      console.log('🔔 Hook: Registro en evento', eventData);
      
      const { userId, eventTitle, eventId } = eventData;
      
      if (!userId || !eventTitle) {
        console.warn('⚠️ Datos incompletos para notificación de evento');
        return;
      }
      
      await NotificationManager.createNotification({
        userId,
        title: 'Registro exitoso en evento',
        message: `Te has registrado exitosamente en el evento "${eventTitle}"`,
        notificationType: 'event',
        relatedEntityType: 'event',
        relatedEntityId: eventId
      });
      
      console.log('✅ Notificación de registro en evento enviada');
      
    } catch (error) {
      console.error('❌ Error en hook onEventRegistration:', error);
    }
  }, []);
  
  // Notificación para cursos
  const onCourseEnrollment = useCallback(async (courseData) => {
    try {
      console.log('🔔 Hook: Inscripción en curso', courseData);
      
      const { userId, courseTitle, courseId } = courseData;
      
      if (!userId || !courseTitle) {
        console.warn('⚠️ Datos incompletos para notificación de curso');
        return;
      }
      
      await NotificationManager.createNotification({
        userId,
        title: 'Inscripción exitosa en curso',
        message: `Te has inscrito exitosamente en el curso "${courseTitle}"`,
        notificationType: 'course',
        relatedEntityType: 'course',
        relatedEntityId: courseId
      });
      
      console.log('✅ Notificación de inscripción en curso enviada');
      
    } catch (error) {
      console.error('❌ Error en hook onCourseEnrollment:', error);
    }
  }, []);
  
  // Notificación de sistema general
  const onSystemNotification = useCallback(async (notificationData) => {
    try {
      console.log('🔔 Hook: Notificación de sistema', notificationData);
      
      const { 
        userId, 
        title, 
        message, 
        type = 'system',
        entityType = null,
        entityId = null 
      } = notificationData;
      
      if (!userId || !title || !message) {
        console.warn('⚠️ Datos incompletos para notificación de sistema');
        return;
      }
      
      await NotificationManager.createNotification({
        userId,
        title,
        message,
        notificationType: type,
        relatedEntityType: entityType,
        relatedEntityId: entityId
      });
      
      console.log('✅ Notificación de sistema enviada');
      
    } catch (error) {
      console.error('❌ Error en hook onSystemNotification:', error);
    }
  }, []);
  
  return {
    onJobApplicationReceived,
    onJobApplicationStatusChanged,
    onUserRegistered,
    onEventRegistration,
    onCourseEnrollment,
    onSystemNotification
  };
};

export default useNotificationActions;

// También exportar las funciones individuales
export {
  useNotificationActions
};